import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  CancelQuoteResult,
  CancelQuoteSuccess,
  CancelReason,
  GetCancelQuoteRequest,
} from '@/features/cancellationQuote/types';
import {
  prepareQuoteDataForPrint,
  type PrintQuoteData,
} from '@/features/cancellationQuote/utils/printHelpers';
import type { ContractDetail } from '@/features/contractSearch/types';
import { openCancellationRequestEmail } from '@/lib/emailUtils';
import { printClasses, triggerPrint } from '@/lib/printUtils';
import { useNavigate } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { Mail, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Props interface for the QuoteResultsDisplay component
 */
interface QuoteResultsDisplayProps {
  /** Array of cancellation quote results, including both successful and denied quotes */
  quoteResults: CancelQuoteResult[];
  /** The original quote request data that was submitted */
  submittedQuoteRequest: GetCancelQuoteRequest;
  /** Array of contract details for which quotes were requested */
  selectedContracts: ContractDetail[];
  /** Array of available cancellation reasons for reference */
  cancelReasons: CancelReason[];
}

/**
 * Helper function to find the full cancel reason description by reason ID
 * @param reasonId - The numeric ID of the cancellation reason
 * @param reasons - Array of available cancellation reasons
 * @returns The description of the cancellation reason or 'Unknown Reason' if not found
 */
const getCancelReasonDescription = (
  reasonId: number,
  reasons: CancelReason[]
): string => {
  const reason = reasons.find((r) => r.id === reasonId);
  return reason ? reason.description : 'Unknown Reason';
};

/**
 * Helper function to find a specific contract from the selectedContracts array by its number
 * @param contractNumber - The contract number to search for
 * @param contracts - Array of contract details to search through
 * @returns The matching contract detail or undefined if not found
 */
const getContractDetails = (
  contractNumber: string,
  contracts: ContractDetail[]
): ContractDetail | undefined => {
  return contracts.find((c) => c.contractNumber === contractNumber);
};

/**
 * QuoteResultsDisplay Component
 *
 * Displays the results of cancellation quote requests in both screen and print-optimized formats.
 * Shows detailed information about successful quotes including refund calculations and denial reasons
 * for unsuccessful quotes. Provides print functionality with proper formatting for physical documents.
 *
 * Features:
 * - Dual display modes: screen view with cards and print view with tables
 * - Print functionality with optimized layout and PDF filename control
 * - Comprehensive quote details including financial calculations
 * - Status badges for quote success/denial
 * - Responsive design for different screen sizes
 * - Navigation back to search functionality
 *
 * @param props - Component props containing quote results and related data
 * @returns JSX element rendering the complete quote results display
 */
export function QuoteResultsDisplay({
  quoteResults,
  submittedQuoteRequest,
  selectedContracts,
  cancelReasons,
}: QuoteResultsDisplayProps) {
  const navigate = useNavigate();
  /** Current date for display purposes, set when component mounts */
  const [displayDate, setDisplayDate] = useState<Date | null>(null);
  /** Prepared data structure optimized for print layout */
  const [printData, setPrintData] = useState<PrintQuoteData | null>(null);

  /**
   * Effect to initialize display data when quote results are available
   * Sets the current date and prepares print-optimized data structure
   */
  useEffect(() => {
    if (quoteResults && quoteResults.length > 0) {
      const currentDate = new Date();
      setDisplayDate(currentDate);

      // Prepare print data
      const preparedPrintData = prepareQuoteDataForPrint(
        quoteResults,
        submittedQuoteRequest,
        selectedContracts,
        cancelReasons
      );
      setPrintData(preparedPrintData);
    }
  }, [quoteResults, submittedQuoteRequest, selectedContracts, cancelReasons]);

  // Assuming all selected contracts share the same customer and vehicle info for the header
  // This might need adjustment if quotes can be for different customers/vehicles in one batch
  const primaryContractDetails = selectedContracts[0];
  const customerName = primaryContractDetails?.customerFullName || 'N/A';
  const vehicleInfoString = primaryContractDetails
    ? `${primaryContractDetails.vehicleInfo.year} ${primaryContractDetails.vehicleInfo.make} ${primaryContractDetails.vehicleInfo.model}`
    : 'N/A';
  const vehicleVin = primaryContractDetails?.vehicleInfo.vin || 'N/A';
  const fullVehicleInfo = primaryContractDetails
    ? `${primaryContractDetails.vehicleInfo.year} ${primaryContractDetails.vehicleInfo.make} ${primaryContractDetails.vehicleInfo.model} (VIN: ${primaryContractDetails.vehicleInfo.vin})`
    : 'N/A';

  const formattedCancelDate = submittedQuoteRequest.cancelDate
    ? format(parseISO(submittedQuoteRequest.cancelDate), 'MM/dd/yyyy')
    : 'N/A';

  /**
   * Handles the print button click functionality
   * Temporarily changes document title for PDF filename control and triggers print dialog
   */
  const handlePrintClick = () => {
    // Store the original title
    const originalTitle = document.title;

    // Get the VIN from the first selected contract for the filename
    const primaryVin = primaryContractDetails?.vehicleInfo.vin || 'Unknown';

    // Set the document title to control PDF filename
    document.title = `Cancel Quote ${primaryVin}`;

    // Trigger print
    triggerPrint();

    // Restore original title after a short delay to ensure print dialog has opened
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };

  /**
   * Handles initiating a cancellation request email
   * Opens the user's default email client in a new tab/window
   */
  const handleInitiateCancellationRequest = () => {
    openCancellationRequestEmail(customerName, vehicleVin);
  };

  return (
    <div className='container mx-auto p-4 space-y-6'>
      {/* Print-Only Header - Hidden on screen, visible only when printing */}
      <div className={`${printClasses.printOnly} hidden print:block`}>
        <div className='print:mb-4 print:pb-3 print:border-b-2 print:border-black'>
          <div className='print:flex print:items-center print:justify-between print:mb-3'>
            <img
              src='/tca_logo_dark.webp'
              alt='TCA Logo'
              className='print:h-14 print:w-auto'
            />
            <h1 className='print:text-xl print:font-bold print:absolute print:left-1/2 print:transform print:-translate-x-1/2'>
              Cancellation Quote Summary
            </h1>
          </div>
          <div className='print:grid print:grid-cols-3 print:gap-4 print:text-sm'>
            <div>
              <div className='print:mb-1'>
                <span className='print:font-semibold'>Customer Name:</span>{' '}
                {customerName}
              </div>
              <div className='print:mb-1'>
                <span className='print:font-semibold'>Cancel Date:</span>{' '}
                {formattedCancelDate}
              </div>
              <div>
                <span className='print:font-semibold'>Cancel Mileage:</span>{' '}
                {submittedQuoteRequest.cancelMileage.toLocaleString()}
              </div>
            </div>
            <div>
              <div>
                <span className='print:font-semibold'>Vehicle:</span>{' '}
                {vehicleInfoString}
                <br />
                <span className='print:font-semibold print:mt-2 print:inline-block'>
                  VIN:
                </span>{' '}
                {vehicleVin}
              </div>
            </div>
            <div>
              <div className='print:mb-1'>
                <span className='print:font-semibold'>Cancel Reason:</span>{' '}
                {getCancelReasonDescription(
                  submittedQuoteRequest.cancelReasonId,
                  cancelReasons
                )}
              </div>
              <div>
                <span className='print:font-semibold'>Quote Date:</span>{' '}
                {displayDate ? format(displayDate, 'MM/dd/yyyy') : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Screen View - Hidden when printing */}
      <div className={printClasses.printHidden}>
        <Card className='mb-6'>
          <CardHeader>
            <div className='flex justify-between items-start'>
              <div>
                <CardTitle className='text-2xl'>
                  Cancellation Quote Summary
                </CardTitle>
                <div className='text-sm mt-2'>
                  Review the details of your cancellation quote request and the
                  results below.
                </div>
              </div>
              <Button
                onClick={handlePrintClick}
                variant='outline'
                size='sm'
                className='flex items-center gap-2'
              >
                <Printer className='h-4 w-4' />
                Print Quote
              </Button>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
              <div>
                <strong>Customer Name:</strong> {customerName}
              </div>
              <div>
                <strong>Vehicle:</strong> {fullVehicleInfo}
              </div>
              <div>
                <strong>Cancel Date:</strong> {formattedCancelDate}
              </div>
              <div>
                <strong>Cancel Mileage:</strong>{' '}
                {submittedQuoteRequest.cancelMileage.toLocaleString()}
              </div>
              <div>
                <strong>Cancel Reason:</strong>{' '}
                {getCancelReasonDescription(
                  submittedQuoteRequest.cancelReasonId,
                  cancelReasons
                )}
              </div>
              <div>
                <strong>Cancellation Quote Date:</strong>{' '}
                {displayDate ? format(displayDate, 'MM/dd/yyyy') : 'N/A'}
              </div>
            </div>
          </CardContent>
        </Card>

        <h2 className='text-xl font-semibold'>Quote Details by Contract:</h2>
      </div>

      {/* Print-Optimized Tables - Hidden on screen, visible only when printing */}
      <div className='hidden print:block'>
        {printData && printData.successfulQuotes.length > 0 && (
          <div className={`${printClasses.printNoBreak} mb-4`}>
            <h3 className='print:text-base print:font-bold print:mb-2'>
              Successful Quotes
            </h3>
            <div className='print:overflow-visible'>
              <Table className='print:w-full print:border-collapse print:border print:border-black successful-quotes-table'>
                <TableHeader>
                  <TableRow className='print:border-b print:border-black'>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Contract
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Product
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Dealer
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Effective Date
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Plan Term
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Pro-Rata Factor
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Original Price
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Cancel Fee
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Claims Deducted
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Refund Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {printData.successfulQuotes.map((quote, index) => (
                    <TableRow
                      key={index}
                      className='print:border-b print:border-black'
                    >
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.contractNumber}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.productType}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.issuingDealer}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white'>
                        {quote.effectiveDate}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white'>
                        {quote.planTerm}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white'>
                        {quote.proRataFactor}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:text-right'>
                        {quote.originalPrice}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:text-right'>
                        {quote.cancellationFee}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:text-right'>
                        {quote.claimsDeducted}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:text-right print:font-bold'>
                        {quote.refundAmount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {printData && printData.deniedQuotes.length > 0 && (
          <div className={`${printClasses.printNoBreak} mb-4`}>
            <h3 className='print:text-base print:font-bold print:mb-2'>
              Denied Quotes
            </h3>
            <div className='print:overflow-visible'>
              <Table className='print:w-full print:border-collapse print:border print:border-black denied-quotes-table'>
                <TableHeader>
                  <TableRow className='print:border-b print:border-black'>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Contract
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Product
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Dealer
                    </TableHead>
                    <TableHead className='print:border print:border-black print:p-1 print:text-xs print:font-bold print:text-black print:bg-white'>
                      Denial Reason
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {printData.deniedQuotes.map((quote, index) => (
                    <TableRow
                      key={index}
                      className='print:border-b print:border-black'
                    >
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.contractNumber}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.productType}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.issuingDealer}
                      </TableCell>
                      <TableCell className='print:border print:border-black print:p-1 print:text-xs print:text-black print:bg-white print:break-words'>
                        {quote.denialReason}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Cancellation Instructions - Hidden on screen, visible only when printing */}
      <div
        className={`${printClasses.printOnly} hidden print:block print:mt-4 print:mb-0`}
      >
        <div className='print:text-sm print:font-bold print:text-black'>
          * To request cancellation of these products send the request to
          cancellations@landcar.com with any supporting documentation
        </div>
      </div>

      {/* Screen View - Individual Contract Cards - Hidden when printing */}
      <div className={printClasses.printHidden}>
        {quoteResults.map((resultItem, index) => {
          const originalContract = getContractDetails(
            resultItem.contractNumber,
            selectedContracts
          );
          if (resultItem.status === 'denied') {
            console.debug(
              `Contract ${resultItem.contractNumber} Denial reason: ${resultItem.denialReason}`
            );
          }

          return (
            <Card key={index} className='mb-4'>
              <CardHeader>
                <div className='flex justify-between items-start'>
                  <div>
                    <CardTitle>Contract: {resultItem.contractNumber}</CardTitle>
                    {originalContract && (
                      <>
                        <div className='mt-2 text-sm'>
                          <strong>Product:</strong> {resultItem.productTypeName}
                        </div>
                        <div className='mt-2 text-sm'>
                          <strong>Issuing Dealer:</strong>{' '}
                          {originalContract.issuingDealer
                            .split(' ')
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(' ')}
                        </div>
                      </>
                    )}
                  </div>
                  <Badge
                    variant={
                      resultItem.status === 'quoted' ? 'default' : 'destructive'
                    }
                  >
                    {resultItem.status === 'quoted'
                      ? 'Quote Generated'
                      : 'Quote Denied'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {resultItem.status === 'quoted' &&
                  (() => {
                    const quotedResult = resultItem as CancelQuoteSuccess;
                    const factors = quotedResult.allFactors?.join(', ');
                    return (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell>Contract Effective Date</TableCell>
                            <TableCell className='text-right'>
                              {format(
                                parseISO(quotedResult.effectiveDate),
                                'MM/dd/yyyy'
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Plan Term</TableCell>
                            <TableCell className='text-right'>
                              {quotedResult.planTerm}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Pro-Rata Factor</TableCell>
                            <TableCell className='text-right'>
                              {factors}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Original Customer Price</TableCell>
                            <TableCell className='text-right'>
                              ${quotedResult.customerPrice.toFixed(2)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Sales Tax Included in Refund</TableCell>
                            <TableCell className='text-right'>
                              ${quotedResult.salesTax.toFixed(2)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Cancellation Fee</TableCell>
                            <TableCell className='text-right'>
                              ${quotedResult.fee.toFixed(2)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              Claims Deducted From Cancellation (If Applicable)
                            </TableCell>
                            <TableCell className='text-right'>
                              ${quotedResult.totalClaims.toFixed(2)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className='text-lg font-bold'>
                              Refund Amount
                            </TableCell>
                            <TableCell className='text-right text-lg font-bold'>
                              ${quotedResult.customerRefund.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    );
                  })()}
                {resultItem.status === 'denied' &&
                  (() => {
                    return (
                      <p className='text-destructive'>
                        <strong className='font-semibold'>
                          Denial Reason:
                        </strong>{' '}
                        {resultItem.denialReason}
                      </p>
                    );
                  })()}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* How to Submit Request for Cancellation */}
      <Card
        className={`mt-8 print:mt-4 bg-accent/5 border-accent/20 ${printClasses.printNoBreak} cancellation-instructions-card ${printClasses.printHidden}`}
      >
        <CardContent className='print:pt-4'>
          <div className='text-sm text-foreground print:text-xs print:text-black print:text-left print:bg-transparent'>
            <div className='font-bold mb-3 text-accent print:text-black print:font-bold text-base'>
              How to Submit Request for Cancellation
            </div>
            <div className='text-foreground print:text-black'>
              Contract cancellation requests should be sent to{' '}
              <button
                onClick={handleInitiateCancellationRequest}
                className='font-semibold text-accent hover:text-accent/80 underline print:text-black print:no-underline bg-transparent border-none p-0 cursor-pointer'
              >
                cancellations@landcar.com
              </button>
              , along with all supporting documentation.
            </div>
            {/* Initiate Cancellation Request Button - Hidden when printing */}
            <div
              className={`mt-4 pt-3 border-t border-accent/20 ${printClasses.printHidden}`}
            >
              <Button
                onClick={handleInitiateCancellationRequest}
                variant='default'
                size='sm'
                className='flex items-center gap-2'
              >
                <Mail className='h-4 w-4' />
                Initiate Cancellation Request
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancellation Quote Disclaimer */}
      <Card
        className={`mt-8 print:!-mt-2 bg-muted/20 border-muted ${printClasses.printNoBreak} disclaimer-card print:py-1`}
      >
        <CardContent className='print:pt-1'>
          <div className='text-sm text-foreground print:text-xs print:text-black print:text-left print:bg-transparent'>
            <div className='font-bold mb-2 text-foreground print:text-black print:font-bold'>
              Cancellation Quote Disclaimer
            </div>
            <div className='print:text-black'>
              This cancellation quote is based on information available as of{' '}
              {displayDate ? format(displayDate, 'MM/dd/yyyy') : 'N/A'} and is
              for estimation purposes only. Final refund amounts are subject to
              change and will be determined at the time of actual cancellation
              processing, in accordance with the terms and conditions of the
              applicable contract. Factors such as the effective cancellation
              date, prior claims, and cancellation fees may affect the final
              amount.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Start New Search Button - Hidden when printing */}
      <div className={`mt-8 flex justify-center ${printClasses.printHidden}`}>
        <Button size='lg' onClick={() => navigate({ to: '/search' })}>
          Start New Search
        </Button>
      </div>
    </div>
  );
}
